type Cleanup = () => void;

export function afterWindowLoad(callback: () => void): Cleanup {
    if (document.readyState === 'complete') {
        const timer = window.setTimeout(callback, 0);

        return () => window.clearTimeout(timer);
    }

    window.addEventListener('load', callback, { once: true });

    return () => window.removeEventListener('load', callback);
}

export function onFirstUserInteraction(callback: () => void): Cleanup {
    let handled = false;

    const cleanup = () => {
        window.removeEventListener('pointerdown', handleInteraction, true);
        window.removeEventListener('keydown', handleInteraction, true);
    };
    const handleInteraction = () => {
        if (handled) {
            return;
        }

        handled = true;
        cleanup();
        callback();
    };

    window.addEventListener('pointerdown', handleInteraction, { capture: true, passive: true });
    window.addEventListener('keydown', handleInteraction, { capture: true });

    return cleanup;
}

export function whenUsercentricsReady(callback: () => void): Cleanup {
    const cmp = document.getElementById('usercentrics-cmp');
    const usercentrics = window as Window & {
        UC_UI?: {
            isInitialized?: () => boolean;
        };
    };

    if (!cmp || usercentrics.UC_UI?.isInitialized?.()) {
        callback();

        return () => undefined;
    }

    window.addEventListener('UC_UI_INITIALIZED', callback, { once: true });

    return () => window.removeEventListener('UC_UI_INITIALIZED', callback);
}

export function whenBrowserIdle(callback: () => void, timeout = 4000): Cleanup {
    if (typeof window.requestIdleCallback === 'function') {
        const idleCallback = window.requestIdleCallback(callback, { timeout });

        return () => window.cancelIdleCallback(idleCallback);
    }

    let timer: number | undefined;
    const cleanupLoad = afterWindowLoad(() => {
        // Safari does not expose requestIdleCallback. Waiting until after load
        // plus a short quiet period keeps monitoring work off hydration/paint.
        timer = window.setTimeout(callback, 1000);
    });

    return () => {
        cleanupLoad();

        if (timer !== undefined) {
            window.clearTimeout(timer);
        }
    };
}
